import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import type { OnChanges, OnInit, SimpleChanges, Type } from '@angular/core';
import { afterNextRender, Component, computed, inject, Injector, input, isDevMode, signal } from '@angular/core';

import { captureException } from 'src/services/sentry/sentry-lite';

import type { AbstractWidget } from '../../abstract-widget';
import { EAGER_WIDGETS_COUNT } from '../../entities/const';
import type { TRenderContext, Widget, WidgetInputData } from '../../entities/types';
import { isHiddenOnDesktop, isHiddenOnMobile } from '../../helpers/widget-spacing.utils';
import { WidgetsService } from '../../widgets.service';

@Component({
    selector: 'website-widget-loader',
    templateUrl: './widget-loader.component.html',
    styleUrls: ['./widget-loader.component.scss'],
    host: {
        '[class.widget--mobile-hidden]': 'isMobileHidden()',
        '[class.widget--desktop-hidden]': 'isDesktopHidden()',
        '[class.widget--hide-figured-bg-mobile]': '!isFiguredBackgroundAllowedMobile()',
        '[class.widget--hide-figured-bg-desktop]': '!isFiguredBackgroundAllowedDesktop()',
    },
    imports: [NgComponentOutlet, NgTemplateOutlet],
})
export class WidgetLoaderComponent implements OnChanges, OnInit {
    private readonly widgetsService = inject(WidgetsService);
    private readonly injector = inject(Injector);

    widget = input.required<Widget & WidgetInputData>();
    index = input(0);
    hasWrapper = input(true);
    renderContext = input<TRenderContext>('default');

    hasReducedTopSpacingMobile = input(false);
    hasReducedTopSpacingDesktop = input(false);
    isFiguredBackgroundAllowedMobile = input(true);
    isFiguredBackgroundAllowedDesktop = input(true);

    readonly componentType = signal<Type<AbstractWidget> | null>(null);
    readonly outletInputs = signal<Record<string, unknown>>({});

    readonly isMobileHidden = computed(() => isHiddenOnMobile(this.widget()));
    readonly isDesktopHidden = computed(() => isHiddenOnDesktop(this.widget()));

    /**
     * First-screen widgets count toward waitForWidgets / APP_IS_LOADED.
     * Below-fold widgets (viewport hydrate in their own templates) must not.
     */
    readonly isEagerWidget = computed(() => this.index() < EAGER_WIDGETS_COUNT);

    private hasMarkedHostPresent = false;
    private hasMarkedHydrated = false;

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['widget'] || changes['index'] || changes['hasWrapper'] || changes['renderContext']) {
            this.setupDeclarativeOutlet();
        }
    }

    public ngOnInit(): void {
        if (!this.componentType()) {
            this.setupDeclarativeOutlet();
        }
    }

    public clear(): void {
        this.componentType.set(null);
        this.outletInputs.set({});
    }

    private setupDeclarativeOutlet(): void {
        const widget = this.widget();

        if (!widget?.class_name) {
            captureException(new Error('Widget is missing class_name'), {
                extra: { widgetIndex: this.index(), renderPath: 'declarative' },
            });

            this.componentType.set(null);
            this.outletInputs.set({});
            this.markReady();
            return;
        }

        if (typeof performance !== 'undefined') {
            performance.mark('Start rendering ' + widget.class_name);
        }

        try {
            const widgetClass = this.widgetsService.getResolvedWidgetType(widget.class_name);

            if (!widgetClass) {
                captureException(new Error(`Missing resolved widget type for ${widget.class_name}`), {
                    tags: { widget: widget.class_name },
                    extra: {
                        widget: widget.class_name,
                        widgetIndex: this.index(),
                        renderPath: 'declarative',
                    },
                });
                this.componentType.set(null);
                this.markReady();
                return;
            }

            this.componentType.set(widgetClass);
            this.outletInputs.set(this.buildOutletInputs());
            this.markReady();
        } catch (error: unknown) {
            this.markReady();
            captureException(error, {
                tags: {
                    widget: widget.class_name,
                },
                extra: {
                    widget: widget.class_name,
                    widgetIndex: this.index(),
                    renderPath: 'declarative',
                },
            });
            if (isDevMode()) {
                console.error(error);
            }
            this.componentType.set(null);
        }
    }

    private buildOutletInputs(): Record<string, unknown> {
        const widget = this.widget();
        const inputs: Record<string, unknown> = {
            widgetData: widget,
            hasWrapper: this.hasWrapper(),
            renderContext: this.renderContext(),
            reducedTopSpacingMobile: this.hasReducedTopSpacingMobile(),
            reducedTopSpacingDesktop: this.hasReducedTopSpacingDesktop(),
            index: this.index(),
        };

        if (widget.personalizationData) {
            inputs['personalizationData'] = widget.personalizationData;
        }

        return inputs;
    }

    private markReady(): void {
        if (!this.hasMarkedHostPresent) {
            this.hasMarkedHostPresent = true;
            this.widgetsService.markWidgetHostPresent();
        }

        if (this.hasMarkedHydrated || !this.isEagerWidget()) {
            return;
        }

        // Below-fold widgets hydrate via their own @defer boundaries and must not
        // count toward the ATF waitForWidgets target. For ATF, wait until the outlet
        // has rendered (not merely until type/input signals are set).
        this.hasMarkedHydrated = true;
        afterNextRender(
            () => {
                this.widgetsService.markWidgetHydrated();
            },
            { injector: this.injector }
        );
    }
}
